import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Mail, KeyRound, CheckCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code+password, 3=done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setStep(2);
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { email, code, new_password: newPassword });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
    }
    setLoading(false);
  };

  // Step 3: Success
  if (step === 3) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <Card className="sm:mx-auto sm:w-full sm:max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="font-display text-2xl uppercase tracking-wide mb-4">Password Reset!</h2>
            <p className="text-muted-foreground mb-6">Your password has been updated. You can now sign in with your new password.</p>
            <Button onClick={() => navigate('/login')} className="rounded-full" data-testid="back-to-login-btn">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
          <img src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" alt="Elevated Roping Media" className="h-12 w-auto" />
          <div className="flex flex-col">
            <span className="font-display font-bold text-sm tracking-tight text-foreground group-hover:text-primary transition-colors leading-none">ELEVATED</span>
            <span className="font-display font-bold text-xs tracking-wider text-muted-foreground leading-none">ROPING MEDIA</span>
          </div>
        </Link>
      </div>

      <Card className="sm:mx-auto sm:w-full sm:max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="font-display text-2xl uppercase tracking-wide text-center">
            {step === 1 ? 'Reset Password' : 'Enter Reset Code'}
          </CardTitle>
          <CardDescription className="text-center">
            {step === 1
              ? 'Enter your email and we\'ll send a reset code to your phone'
              : `We sent a 6-digit code via SMS to the phone on file for ${email}`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 1 ? (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide" data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="code">6-Digit Code</Label>
                <Input id="code" type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required className="h-12 text-center text-2xl tracking-[0.5em] font-mono" data-testid="reset-code-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input id="new-password" type={showPassword ? 'text' : 'password'} placeholder="Minimum 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="h-12" data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input id="confirm-password" type={showPassword ? 'text' : 'password'} placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-12" data-testid="confirm-password-input" />
              </div>
              <Button type="submit" disabled={loading || code.length < 6} className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide" data-testid="reset-password-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <button type="button" onClick={() => { setStep(1); setError(''); setCode(''); }} className="w-full text-sm text-muted-foreground hover:text-foreground text-center">
                Didn't receive a code? Go back and try again
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-primary font-semibold hover:underline">Back to Sign In</Link>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 text-center">
        <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
